import { IonItem, IonLabel } from '@ionic/react';
import { useCallback } from 'react';
import {
  GetScratchpadPlaylistActivatedDocument,
  ListScratchpadPlaylistsDocument,
  ScratchpadPlaylist,
  useListScratchpadPlaylistsQuery,
  useUpdateScratchpadPlaylistMutation,
} from '../../../api/graphql/generated';
import { trackPlaylistLoad } from '../../../config/analytics';
import i18n from '../../../i18n';
import {
  SearchRowRenderer,
  useSearchPopover,
} from '../../ui/searchbar/searchbar';

interface ScratchpadPlaylistSearchPopoverProps {
  machineId?: string;
}

const useScratchpadPlaylistSearchPopover = (
  props: ScratchpadPlaylistSearchPopoverProps
) => {
  const { machineId } = props;

  const [updatePlaylist] = useUpdateScratchpadPlaylistMutation();

  const searchRowRenderer = useCallback<SearchRowRenderer<ScratchpadPlaylist>>(
    ({ item, dismiss }) => {
      return (
        <IonItem
          key={item.id}
          button={true}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            await updatePlaylist({
              variables: {
                input: {
                  id: item.id,
                  machineId: item.machineId,
                  // auto activate the scratchpad that we click on
                  activated: true,
                },
              },
              // refire get playlist activated query to make sure cache isn't stale
              // the list playlists document isn't really needed however makes it more visually
              // appealing in that the correct playlist shows "loaded" when opening the search
              // after loading a new playlist
              refetchQueries: [
                GetScratchpadPlaylistActivatedDocument,
                ListScratchpadPlaylistsDocument,
              ],
            });

            trackPlaylistLoad();

            dismiss();
          }}
        >
          <IonLabel>
            <h2>{item.name}</h2>
          </IonLabel>
          {item.activated && (
            <IonLabel color="primary" slot="end">
              {i18n.t('common:scratchpad_loaded')}
            </IonLabel>
          )}
        </IonItem>
      );
    },
    [updatePlaylist]
  );

  const { showSearch } = useSearchPopover({
    options: {
      useQuery: useListScratchpadPlaylistsQuery,
      input: { machineId: machineId as string, limit: 20 },
      queryOptions: {
        fetchPolicy: 'no-cache',
        skip: machineId === undefined,
      },
    },
    rowRenderer: searchRowRenderer,
    placeholder: i18n.t('common:scratchpad_playlist_search'),
    forceSearchOnDidPresent: true,
  });

  return showSearch;
};

export { useScratchpadPlaylistSearchPopover };
